import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, {useCallback, useEffect, useRef, useState} from 'react'

import useIsMobile from 'hooks/useIsMobile'
import useParallaxEnabled from 'hooks/useParallaxEnabled'

import {ERA_GLYPHS} from './eraGlyphs'
import logoDark from './assets/keyrus-ai-dark.svg'

const HEADER_LINE_1 = 'AI does not transform businesses.'
const HEADER_LINE_2 = 'Architected intelligence does.'
const HEADER_LINE_3 = 'And Keyrus has been doing this for over 30 years:'
const LOGO_ALT = 'keyrus Ai — Architect of intelligence'

const ACTIVE_INK = '#07101f'
const INACTIVE_INK = '#303030'
const SURFACE = '#ffffff'

const COLOR_TRANSITION = 'fill 0.45s ease, stroke 0.45s ease, color 0.45s ease'
const REVEAL_TRANSITION =
  'opacity 0.8s ease, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)'

// Shared with KeyrusAiNav: the nav dispatches this on every item click so a
// section can replay its entrance animation when its own anchor is targeted.
const NAV_CLICK_EVENT = 'keyrus-ai-nav'
const ERAS_ANCHOR = '#intelligence-eras'

const NAV_OFFSET = 90
const ICON_SIZE = 64
const RAIL_GAP = 56
const SCROLL_SCALE = 1
// Desktop scroll effect: eras start spread by spreadGap and, as you scroll, each
// one slides up toward the previous (sequentially from the top) until the gaps
// tighten to stackGap — the eras pile up under the first one. On tall screens the
// gap widens (up to MAX) to fill the stage; on short ones it holds at MIN — the
// gap that still clears the tallest era's body so paragraphs never overlap — and
// the whole stage is scaled down instead (fitScale) so the last era still fits and
// clears the Contact card. MIN is a no-overlap floor, not a compression target.
const MIN_STACK_GAP = 150
const MAX_STACK_GAP = 240
// Extra breathing room added on top of the tallest measured body when deriving
// the no-overlap gap floor (see stackGap below), so paragraphs never touch.
const BODY_GAP_CLEARANCE = 28
// The pile is scaled down (fitScale) so the whole thing — at the no-overlap gap —
// fits the stage. With long copy this means smaller text, which is preferred
// over overlapping or clipped content. Low floor so everything stays visible.
const MIN_PILE_SCALE = 0.4
const SPREAD_TRAVEL = 130
const STACK_TOP_INSET = 30
// Room kept below the last era's center for the logo's lower half (≈54px at 320px
// wide, viewBox 354×120) plus clearance before the Contact card's rounded top.
const LAST_ITEM_RESERVE = 110
// Compression finishes at this scroll fraction; the stacked pile then holds on
// screen for the remaining range before the Contact card slides up over it.
const COMPRESS_END = 0.8
// Static (reduce-motion) desktop spaces eras only via each row's min-height,
// with the content centered inside it. Adding to every row widens the gap
// between eras uniformly while preserving their relative height rhythm.
const DESKTOP_ROW_EXTRA = 150

const ERAS = [
  {
    id: 'business',
    period: '1990 – 2000',
    title: 'Business intelligence',
    body: 'We worked with organizations to build their first real data foundations. Through data warehousing and ETL, we connected what was fragmented and gave companies something many had never had before: a single, structured version of the truth.',
    icon: 'briefcase',
    height: 260,
    heightMobile: 150,
  },
  {
    id: 'digital',
    period: '2000-2012',
    title: 'Digital intelligence',
    body: 'We helped organizations connect the dots between internal systems and the expanding digital landscape. We built the solutions that made live dashboards possible. We put real-time intelligence into the hands of leaders who were making decisions at the speed their businesses demanded.',
    icon: 'monitor',
    height: 380,
    heightMobile: 190,
  },
  {
    id: 'data',
    period: '2012 – 2015',
    title: 'Data intelligence',
    body: 'Keyrus helped organizations make sense of big data. We helped find patterns in the noise. Intelligence was no longer just about knowing what happened or what was happening right now. It became about discovering what was hidden.',
    icon: 'database',
    height: 420,
    heightMobile: 200,
  },
  {
    id: 'predictive',
    period: '2015 – 2022',
    title: 'Predictive intelligence',
    body: 'We helped our clients move from "here’s what happened" to "here’s what’s coming next," building the models and capabilities that made prediction operational, not just experimental.',
    icon: 'orb',
    height: 460,
    heightMobile: 220,
  },
  {
    id: 'augmented',
    period: '2022 – 2025',
    title: 'Augmented intelligence',
    body: 'Keyrus helped bring generative and agentic AI into the enterprise in ways that augmented human decision-making rather than replacing it. We saw AI and human intelligence as partners, not competitors.',
    icon: 'phone',
    height: 540,
    heightMobile: 250,
  },
  {
    id: 'architected',
    period: '2026',
    title: 'Architected Intelligence',
    body: null,
    icon: 'architected',
    isFinal: true,
    height: 320,
    heightMobile: 180,
  },
]

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const lerp = (from, to, progress) => from + (to - from) * progress

const getStyles = (theme, isMobile) => {
  const palette = theme.palette || {}
  const text = palette.text || {}
  const surface = palette.background?.default || SURFACE
  const black = text.dark || '#000000'
  const ink = INACTIVE_INK
  const iconSize = isMobile ? 68 : 64

  return {
    root: {
      width: '100%',
      background: surface,
      color: black,
      padding: isMobile ? '64px 16px 0' : '0 24px 0',
      fontFamily: "'Montserrat', sans-serif",
      scrollMarginTop: isMobile ? 20 : 90,
    },
    inner: {
      maxWidth: 1180,
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      paddingTop: isMobile ? 8 : 112,
      marginBottom: isMobile ? 40 : 24,
    },
    headerLine1: {
      margin: 0,
      color: black,
      fontWeight: 400,
      fontSize: isMobile ? 16 : 40,
      lineHeight: 1.18,
    },
    headerLine2: {
      margin: '4px 0 0',
      color: ink,
      fontWeight: 800,
      fontSize: isMobile ? 22 : 50,
      lineHeight: 1.14,
    },
    headerLine3: {
      margin: isMobile ? '16px 0 0' : '20px 0 0',
      color: black,
      fontWeight: 300,
      fontSize: isMobile ? 16 : 30,
      lineHeight: 1.3,
    },
    timeline: {
      position: 'relative',
      display: isMobile ? 'flex' : undefined,
      flexDirection: isMobile ? 'column' : undefined,
      gap: isMobile ? 23 : undefined,
    },
    axisLine: {
      position: 'absolute',
      top: isMobile ? 75 : 130,
      bottom: 0,
      left: isMobile ? 34 : '50%',
      width: 2,
      transform: 'translateX(-50%)',
      background: 'rgba(7, 16, 31, 0.4)',
      zIndex: 1,
      pointerEvents: 'none',
    },
    row: {
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: isMobile ? 'flex-start' : 'center',
      transition: REVEAL_TRANSITION,
    },
    left: {
      width: 440,
      flex: '0 0 auto',
      textAlign: 'right',
    },
    mobileContent: {
      flex: '1 1 0',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    period: {
      margin: 0,
      color: black,
      fontWeight: 400,
      fontSize: isMobile ? 17 : 30,
      lineHeight: 1.13,
    },
    title: {
      margin: 0,
      color: black,
      fontWeight: 700,
      fontSize: isMobile ? 17 : 30,
      lineHeight: 1.13,
    },
    iconCell: {
      position: 'relative',
      alignSelf: 'stretch',
      flex: '0 0 auto',
      width: iconSize,
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      margin: isMobile ? '0 27px 0 0' : '0 60px',
    },
    iconBox: {
      position: 'relative',
      zIndex: 1,
      width: iconSize,
      height: iconSize,
      flex: '0 0 auto',
    },
    icon: {
      display: 'block',
      width: '100%',
      height: '100%',
    },
    right: {
      width: isMobile ? 'auto' : 440,
      flex: '0 0 auto',
    },
    body: {
      margin: 0,
      color: black,
      fontWeight: 400,
      fontSize: isMobile ? 12 : 18,
      lineHeight: isMobile ? 1.4 : 1.3,
    },
    finalLogoSlot: {
      width: isMobile ? 'auto' : 440,
      flex: isMobile ? '1 1 0' : '0 0 auto',
      display: 'flex',
      justifyContent: 'flex-start',
    },
    finalLogo: {
      width: isMobile ? 'min(220px, 64vw)' : 320,
      maxWidth: '100%',
      height: 'auto',
    },
    track: {
      position: 'relative',
      height: `calc(${
        (ERAS.length - 1) * SCROLL_SCALE + 1
      } * (100vh - ${NAV_OFFSET}px))`,
      marginTop: 32,
      marginBottom: `calc(100vh - ${NAV_OFFSET}px)`,
    },
    stage: {
      position: 'sticky',
      top: NAV_OFFSET,
      height: `calc(100vh - ${NAV_OFFSET}px)`,
      overflow: 'hidden',
      zIndex: 1,
    },
    stageInner: {
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'center',
      gap: RAIL_GAP,
      transformOrigin: 'top center',
    },
    sideColumn: {
      position: 'relative',
      flex: '1 1 0',
      minWidth: 0,
      // Wider than before: fewer wrapped lines means shorter bodies, so the pile
      // needs less down-scaling and the text stays larger. There is ample
      // horizontal room beside the centered rail on desktop.
      maxWidth: 560,
    },
    rail: {
      position: 'relative',
      flex: '0 0 auto',
      width: ICON_SIZE,
    },
    railInner: {
      position: 'absolute',
      left: 0,
      width: ICON_SIZE,
    },
    railLine: {
      position: 'absolute',
      left: ICON_SIZE / 2 - 1,
      width: 2,
      background: 'rgba(7, 16, 31, 0.4)',
      zIndex: 0,
    },
    railBubble: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: ICON_SIZE,
      height: ICON_SIZE,
      zIndex: 1,
    },
    headingItem: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 8,
      textAlign: 'right',
    },
    bodyItem: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 8,
    },
  }
}

const renderIcon = (era, styles, isActive) => {
  const glyph = ERA_GLYPHS[era.icon]
  const iconFill = isActive ? SURFACE : ACTIVE_INK

  return (
    <svg
      aria-hidden="true"
      fill="none"
      style={{...styles.icon, transition: COLOR_TRANSITION}}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        height="118"
        rx="59"
        style={{
          fill: isActive ? ACTIVE_INK : SURFACE,
          stroke: ACTIVE_INK,
          strokeWidth: 2,
          transition: COLOR_TRANSITION,
        }}
        width="118"
        x="1"
        y="1"
      />
      <g
        fill={iconFill}
        style={{transition: COLOR_TRANSITION}}
        transform={glyph?.transform}
      >
        {glyph?.paths.map(d => (
          <path d={d} key={d} />
        ))}
      </g>
    </svg>
  )
}

const KeyrusAiEras = ({copy}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const parallaxEnabled = useParallaxEnabled()
  const styles = getStyles(theme, isMobile)

  const headerLine1 = copy.headerLine1 || HEADER_LINE_1
  const headerLine2 = copy.headerLine2 || HEADER_LINE_2
  const headerLine3 = copy.headerLine3 || HEADER_LINE_3
  const eraBodies = copy.bodies || {}
  const eraTitles = copy.titles || {}
  const eras = ERAS.map(era => ({
    ...era,
    title: eraTitles[era.id] || era.title,
    body: eraBodies[era.id] || era.body,
  }))

  const rowRefs = useRef({})
  const [activeIds, setActiveIds] = useState({})
  const [revealedIds, setRevealedIds] = useState({})
  // Bumped whenever the section-2 nav item is clicked so the reveal effect
  // re-runs and replays the slide-in even when the section is already on screen.
  const [replayNonce, setReplayNonce] = useState(0)

  const registerRow = useCallback(
    id => node => {
      rowRefs.current[id] = node
    },
    [],
  )

  useEffect(() => {
    if (typeof window === 'undefined' || parallaxEnabled) {
      return undefined
    }

    if (!('IntersectionObserver' in window)) {
      setActiveIds(ERAS.reduce((acc, era) => ({...acc, [era.id]: true}), {}))

      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        setActiveIds(prev => {
          const next = {...prev}

          entries.forEach(entry => {
            const {eraId} = entry.target.dataset

            if (entry.isIntersecting) {
              next[eraId] = true
            } else {
              delete next[eraId]
            }
          })

          return next
        })
      },
      {
        rootMargin: '-35% 0px -45% 0px',
        threshold: 0.1,
      },
    )

    const nodes = ERAS.map(era => rowRefs.current[era.id]).filter(Boolean)

    nodes.forEach(node => observer.observe(node))

    return () => observer.disconnect()
  }, [isMobile, parallaxEnabled])

  useEffect(() => {
    if (typeof window === 'undefined' || parallaxEnabled) {
      return undefined
    }

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      setRevealedIds(ERAS.reduce((acc, era) => ({...acc, [era.id]: true}), {}))

      return undefined
    }

    // Start hidden, then let the (re)connected observer reveal whatever is in
    // view. observe() always emits an initial entry, so on a replay this fires
    // again for rows already on screen — re-triggering the slide-in.
    setRevealedIds({})

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const {eraId} = entry.target.dataset

            setRevealedIds(prev => ({...prev, [eraId]: true}))
          }
        })
      },
      {rootMargin: '0px 0px -12% 0px', threshold: 0.12},
    )

    const nodes = ERAS.map(era => rowRefs.current[era.id]).filter(Boolean)

    nodes.forEach(node => observer.observe(node))

    return () => observer.disconnect()
  }, [isMobile, parallaxEnabled, replayNonce])

  const sectionRef = useRef(null)

  // When the section fully leaves the viewport, wipe revealedIds so the
  // slide-in animations replay the next time the user scrolls back into section 2.
  useEffect(() => {
    if (typeof window === 'undefined' || parallaxEnabled) {
      return undefined
    }

    if (!('IntersectionObserver' in window) || !sectionRef.current) {
      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0].isIntersecting) {
          setRevealedIds({})
        }
      },
      {threshold: 0},
    )

    observer.observe(sectionRef.current)

    return () => observer.disconnect()
  }, [parallaxEnabled])

  // Replay the entrance animation each time the section-2 nav item is clicked,
  // even if it's the current hash (a same-hash click fires no hashchange).
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const onNavClick = event => {
      if (event.detail?.href === ERAS_ANCHOR) {
        setReplayNonce(nonce => nonce + 1)
      }
    }

    window.addEventListener(NAV_CLICK_EVENT, onNavClick)

    return () => window.removeEventListener(NAV_CLICK_EVENT, onNavClick)
  }, [])

  const trackRef = useRef(null)
  const railRef = useRef(null)
  const bodyRefs = useRef({})
  const [metrics, setMetrics] = useState({
    progress: 0,
    panelHeight: 800,
    maxBodyHeight: 0,
  })

  useEffect(() => {
    if (!parallaxEnabled || typeof window === 'undefined') {
      return undefined
    }

    const frameRef = {current: null}

    const measure = () => {
      frameRef.current = null
      const trackEl = trackRef.current
      const railEl = railRef.current

      if (!trackEl || !railEl) {
        return
      }

      const trackRect = trackEl.getBoundingClientRect()
      const panelHeight = railEl.getBoundingClientRect().height
      const denom = trackRect.height - panelHeight
      const progress = denom > 0 ? clamp(-trackRect.top / denom, 0, 1) : 0

      // Tallest rendered body — drives the no-overlap gap. offsetHeight is the
      // laid-out height and is unaffected by the translate/scale transforms.
      const bodyHeights = Object.values(bodyRefs.current)
        .filter(Boolean)
        .map(el => el.offsetHeight)
      const maxBodyHeight = bodyHeights.length ? Math.max(...bodyHeights) : 0

      setMetrics({progress, panelHeight, maxBodyHeight})
    }

    const onScroll = () => {
      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(measure)
      }
    }

    measure()
    // Scroll events don't bubble, so a plain window listener misses scrolling
    // that happens inside an overflow container (e.g. the gateway overlay).
    // Capturing on window catches both window scroll and any descendant
    // scrollable element, so the parallax works on the page and in the modal.
    window.addEventListener('scroll', onScroll, {passive: true, capture: true})
    window.addEventListener('resize', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll, {capture: true})
      window.removeEventListener('resize', onScroll)

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
      }
    }
  }, [parallaxEnabled])

  if (parallaxEnabled) {
    const {progress, panelHeight, maxBodyHeight} = metrics
    const stagePos = clamp(
      (progress * (ERAS.length - 1)) / COMPRESS_END,
      0,
      ERAS.length - 1,
    )

    // Per-era gap. Start from the gap that fills the stage (clamped MIN/MAX),
    // then never let it fall below the tallest body so paragraphs never overlap,
    // whatever the locale. The whole pile is then scaled down (fitScale) so it
    // fits the stage — long copy just renders smaller rather than overlapping.
    const firstCenter = STACK_TOP_INSET + ICON_SIZE / 2
    const gapFloor = Math.max(MIN_STACK_GAP, maxBodyHeight + BODY_GAP_CLEARANCE)
    const stackGap = Math.max(
      clamp(
        (panelHeight - LAST_ITEM_RESERVE - firstCenter) / (ERAS.length - 1),
        MIN_STACK_GAP,
        MAX_STACK_GAP,
      ),
      gapFloor,
    )
    const spreadGap = stackGap + SPREAD_TRAVEL
    const naturalPileHeight =
      firstCenter + (ERAS.length - 1) * stackGap + LAST_ITEM_RESERVE
    const fitScale = clamp(panelHeight / naturalPileHeight, MIN_PILE_SCALE, 1)

    const eraStates = eras.map((era, index) => {
      // Each era compresses from its spread slot toward its stacked slot as the
      // scroll passes its segment, so gaps close one after another from the top.
      const localProgress =
        index === 0 ? 1 : clamp(stagePos - (index - 1), 0, 1)
      const bubbleY = lerp(index * spreadGap, index * stackGap, localProgress)
      const centerY = STACK_TOP_INSET + bubbleY + ICON_SIZE / 2
      const isActive = index === 0 || localProgress >= 0.5

      return {era, centerY, isActive, opacity: 1}
    })

    return (
      <section
        aria-labelledby="keyrus-ai-eras-title"
        id="intelligence-eras"
        style={styles.root}
      >
        <div style={styles.inner}>
          <header style={styles.header}>
            <p style={styles.headerLine1}>{headerLine1}</p>
            <h2 id="keyrus-ai-eras-title" style={styles.headerLine2}>
              {headerLine2}
            </h2>
            <p style={styles.headerLine3}>{headerLine3}</p>
          </header>

          <div ref={trackRef} style={styles.track}>
            <div ref={railRef} style={styles.stage}>
              <div
                style={{...styles.stageInner, transform: `scale(${fitScale})`}}
              >
                <div style={styles.sideColumn}>
                  {eraStates.map(({era, centerY, opacity}) => (
                    <div
                      key={era.id}
                      style={{
                        ...styles.headingItem,
                        transform: `translateY(${centerY}px) translateY(-50%)`,
                        opacity,
                      }}
                    >
                      <p style={styles.period}>{era.period}</p>
                      <p style={styles.title}>{era.title}</p>
                    </div>
                  ))}
                </div>

                <div style={styles.rail}>
                  <div style={{...styles.railInner, top: 0, height: '100%'}}>
                    {eraStates.slice(0, -1).map(({centerY}, index) => {
                      const lineTop = centerY + ICON_SIZE / 2
                      const lineHeight = Math.max(
                        0,
                        eraStates[index + 1].centerY - ICON_SIZE / 2 - lineTop,
                      )

                      return (
                        <span
                          aria-hidden="true"
                          key={`line-${ERAS[index].id}`}
                          style={{
                            ...styles.railLine,
                            top: lineTop,
                            height: lineHeight,
                          }}
                        />
                      )
                    })}
                    {eraStates.map(({era, centerY, isActive, opacity}) => (
                      <div
                        key={era.id}
                        style={{
                          ...styles.railBubble,
                          top: centerY - ICON_SIZE / 2,
                          opacity,
                        }}
                      >
                        {renderIcon(era, styles, isActive)}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.sideColumn}>
                  {eraStates.map(({era, centerY, opacity}) => (
                    <div
                      key={era.id}
                      ref={el => {
                        bodyRefs.current[era.id] = el
                      }}
                      style={{
                        ...styles.bodyItem,
                        // Top-aligned to the bubble (not centered): a tall body
                        // centered on its bubble pushes its first lines above the
                        // stage and they get clipped. Starting at the bubble top
                        // keeps the whole paragraph in view, flowing downward.
                        transform: `translateY(${centerY - ICON_SIZE / 2}px)`,
                        opacity,
                      }}
                    >
                      {era.isFinal ? (
                        <div style={{...styles.finalLogoSlot, width: '100%'}}>
                          <img
                            alt={LOGO_ALT}
                            src={logoDark}
                            style={styles.finalLogo}
                          />
                        </div>
                      ) : (
                        <p style={styles.body}>{era.body}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      aria-labelledby="keyrus-ai-eras-title"
      id="intelligence-eras"
      ref={sectionRef}
      style={styles.root}
    >
      <div style={styles.inner}>
        <header style={styles.header}>
          <p style={styles.headerLine1}>{headerLine1}</p>
          <h2 id="keyrus-ai-eras-title" style={styles.headerLine2}>
            {headerLine2}
          </h2>
          <p style={styles.headerLine3}>{headerLine3}</p>
        </header>

        <div style={styles.timeline}>
          <span aria-hidden="true" style={styles.axisLine} />
          {eras.map(era => {
            const isActive = Boolean(activeIds[era.id])
            const isRevealed = Boolean(revealedIds[era.id])

            const iconCell = (
              <div style={styles.iconCell}>
                <div style={styles.iconBox}>
                  {renderIcon(era, styles, isActive)}
                </div>
              </div>
            )

            const heading = (
              <>
                <p style={styles.period}>{era.period}</p>
                <p style={styles.title}>{era.title}</p>
              </>
            )

            const detail = era.isFinal ? (
              <div style={styles.finalLogoSlot}>
                <img alt={LOGO_ALT} src={logoDark} style={styles.finalLogo} />
              </div>
            ) : (
              <div style={styles.right}>
                <p style={styles.body}>{era.body}</p>
              </div>
            )

            return (
              <div
                data-era-id={era.id}
                key={era.id}
                ref={registerRow(era.id)}
                style={{
                  ...styles.row,
                  minHeight: isMobile
                    ? era.heightMobile
                    : era.height + DESKTOP_ROW_EXTRA,
                  opacity: isRevealed ? 1 : 0,
                  transform: isRevealed ? 'translateY(0)' : 'translateY(48px)',
                }}
              >
                {isMobile ? (
                  <>
                    {iconCell}
                    <div style={styles.mobileContent}>
                      {heading}
                      {detail}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={styles.left}>{heading}</div>
                    {iconCell}
                    {detail}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

KeyrusAiEras.propTypes = {
  copy: PropTypes.shape({
    bodies: PropTypes.objectOf(PropTypes.string),
    headerLine1: PropTypes.string,
    headerLine2: PropTypes.string,
    headerLine3: PropTypes.string,
    titles: PropTypes.objectOf(PropTypes.string),
  }),
}

KeyrusAiEras.defaultProps = {
  copy: {},
}

export default KeyrusAiEras
